import React from 'react';
import { supabase } from '../lib/supabase';
import { X, FileText, History, ArrowRight } from 'lucide-react';
import { formatDateColombia } from '../utils/dateFormat';

interface SystemParametersAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  refreshTrigger?: number; // Trigger para refrescar cuando cambia
}

interface AuditLog {
  id: string;
  modifier_name: string;
  parameter_changed: string;
  old_value: string;
  new_value: string;
  created_at: string;
}

const parameterLabels: Record<string, string> = {
  min_price: 'Precio mínimo',
  max_price: 'Precio máximo',
  // Mantener compatibilidad con registros antiguos
  min_credits_per_document: 'Créditos mínimos por documento',
  credit_value: 'Valor del crédito',
  initial_credits: 'Créditos iniciales'
};

// Función para formatear dinero (COP) si el valor es numérico
const formatearValor = (valor: string): string => {
  const numValor = parseFloat(valor);
  if (!isNaN(numValor)) {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numValor);
  }
  return valor;
};

export const SystemParametersAuditModal: React.FC<SystemParametersAuditModalProps> = ({
  isOpen,
  onClose,
  refreshTrigger
}) => {
  const [auditLogs, setAuditLogs] = React.useState<AuditLog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      fetchAuditLogs();
    }
  }, [isOpen, refreshTrigger]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase().rpc('get_system_parameters_audit_log');

      if (fetchError) {
        console.error('Error al obtener historial:', fetchError);
        throw fetchError;
      }
      
      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al obtener el historial de cambios:', error);
      setError('Error al cargar el historial de cambios');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1F2937] rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <History className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Historial de Cambios en Parámetros del Sistema</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchAuditLogs}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              title="Refrescar historial"
            >
              Actualizar
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-red-400 text-center py-12">{error}</div>
          ) : auditLogs.length === 0 ? (
            <div className="text-gray-400 text-center py-12">
              No hay cambios registrados
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Modificado Por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Parámetro
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Valor Anterior
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider w-12">
                      →
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Valor Nuevo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800/30 divide-y divide-gray-700">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-800/50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        {formatDateColombia(log.created_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        {log.modifier_name || 'Sistema'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-400 font-medium">
                        {parameterLabels[log.parameter_changed] || log.parameter_changed}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400 mb-1">Tenía</span>
                          <span className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-semibold bg-red-900/40 text-red-200 border border-red-700/50 w-fit">
                            {formatearValor(log.old_value)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <ArrowRight className="w-5 h-5 text-gray-500 mx-auto" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400 mb-1">Quedó</span>
                          <span className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-semibold bg-green-900/40 text-green-300 border border-green-700/50 w-fit">
                            {formatearValor(log.new_value)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemParametersAuditModal;
